import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.runtime.staticCompositionLocalOf
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

// Small building blocks every screen shares, so the screens themselves stay about their own logic.

/** Installed by App(): drops the stored session when the backend stops accepting it. */
val LocalSessionExpired = staticCompositionLocalOf<() -> Unit> { {} }

/**
 * The busy/error bookkeeping every screen needs. One instance per screen replaces the
 * `try { ... } catch { error = ... } finally { busy = false }` block that otherwise gets copied
 * into every load and every button handler.
 */
class AsyncState(private val scope: CoroutineScope, private val onSessionExpired: () -> Unit) {
    /** True while a request is in flight; buttons disable themselves on it. */
    var busy by mutableStateOf(false)
        private set

    /** False until the first request finishes, so screens know when to show "Načítám…". */
    var loaded by mutableStateOf(false)
        private set

    var error by mutableStateOf<String?>(null)
        private set

    /** Runs [block] from an event handler. */
    fun launch(block: suspend () -> Unit) {
        scope.launch { run(block) }
    }

    /** Runs [block] from inside an existing coroutine (a LaunchedEffect, typically). */
    suspend fun run(block: suspend () -> Unit) {
        busy = true
        error = null
        try {
            block()
        } catch (e: Throwable) {
            error = e.apiMessage()
            if (e.isUnauthorized()) onSessionExpired()
        } finally {
            busy = false
            loaded = true
        }
    }
}

@Composable
fun rememberAsyncState(): AsyncState {
    val scope = rememberCoroutineScope()
    val onSessionExpired = LocalSessionExpired.current
    return remember { AsyncState(scope, onSessionExpired) }
}

/** Shows the screen's error, if it has one. */
@Composable
fun ErrorMessage(message: String?) {
    if (message != null) P(attrs = { classes("error-text") }) { Text("Chyba: $message") }
}

@Composable
fun LoadingText() {
    P { Text("Načítám…") }
}

@Composable
fun NavButton(label: String, active: Boolean, onClick: () -> Unit) {
    Button(attrs = {
        if (active) classes("active")
        onClick { onClick() }
    }) { Text(label) }
}

/** The app's one prominent action button (save, add, unlock). */
@Composable
fun PrimaryButton(label: String, enabled: Boolean = true, onClick: () -> Unit) {
    Button(attrs = {
        classes("btn-primary")
        if (!enabled) disabled()
        onClick { onClick() }
    }) { Text(label) }
}

/** A plain single-line text field; [decimal] asks phones for the numeric keypad. */
@Composable
fun TextField(
    value: String,
    onChange: (String) -> Unit,
    placeholder: String? = null,
    decimal: Boolean = false,
    cssClass: String? = null,
    onEnter: (() -> Unit)? = null,
) {
    Input(type = InputType.Text, attrs = {
        cssClass?.let { classes(it) }
        if (decimal) attr("inputmode", "decimal")
        placeholder?.let { placeholder(it) }
        value(value)
        onInput { onChange(it.value) }
        onEnter?.let { handler -> onKeyDown { if (it.key == "Enter") handler() } }
    })
}

@Composable
fun TierBadge(label: String) {
    Span(attrs = { classes("badge", tierBadgeClass(label)) }) { Text(label) }
}

fun medalIcon(medal: String?): String = when (medal) {
    "gold" -> "🥇"
    "silver" -> "🥈"
    "bronze" -> "🥉"
    else -> "–"
}

fun medalLabel(medal: String?): String = when (medal) {
    "gold" -> "Zlato"
    "silver" -> "Stříbro"
    "bronze" -> "Bronz"
    else -> "Bez medaile"
}

@Composable
fun MedalBadge(medal: String?) {
    Span(attrs = { classes("medal-badge", "medal-${medal ?: "none"}") }) {
        Text("${medalIcon(medal)} ${medalLabel(medal)}")
    }
}

/** Only earned medals, ordered from gold to bronze. */
fun medalSummaryText(totals: MedalTotals?): String = totals?.let {
    listOf("🥇" to it.gold, "🥈" to it.silver, "🥉" to it.bronze)
        .filter { (_, count) -> count > 0 }
        .joinToString("  ") { (icon, count) -> "$icon $count" }
}.orEmpty()

/** Compact lifetime result: every completed challenge contributes to exactly one counter. */
@Composable
fun MedalSummary(totals: MedalTotals?) {
    val summary = medalSummaryText(totals)
    if (summary.isEmpty()) return
    Span(attrs = {
        classes("medal-summary")
    }) { Text(summary) }
}

/**
 * One badge per streak level, strongest tier first, coloured like that tier so the streak and the
 * day's rating speak the same visual language.
 *
 * Every running level shows the number it holds -- today can only carry a level forward, never cut
 * it short, because the day is not over (see [Streak.previewWith]). What the colour says is whether
 * today has already got there:
 *
 *  - carried today   -- full tier colour, and a flame on the strongest one reached
 *  - still standing  -- same tier colour but faded: the run is alive and today has not reached it
 *                       yet, so this is the one to go and earn
 *  - pending         -- dashed with a snowflake: riding on a spent freeze, unrepaid as yet
 *  - not running     -- greyed out, showing its zero
 *
 * [includeInactive] decides whether that last state is drawn. The daily entry screen wants it, to
 * show the whole ladder; a leaderboard column of zeroes in every row says nothing, so there only
 * the running levels appear.
 */
@Composable
fun StreakBadges(preview: StreakPreview, includeInactive: Boolean = false) {
    val levels = preview.levels
    val shown = levels.indices.reversed().filter { includeInactive || levels[it].streak > 0 }
    if (shown.isEmpty()) {
        Span(attrs = { classes("badge-streak-none") }) { Text("–") }
        return
    }
    val flame = shown.firstOrNull { preview.continuedToday(it) }
    shown.forEach { index ->
        val level = levels[index]
        val running = level.streak > 0
        val carried = preview.continuedToday(index)
        Span(attrs = {
            classes("badge-streak")
            classes(if (running) streakBadgeClass(level.label) else "badge-streak-off")
            if (running && !carried && !preview.pending) classes("badge-streak-standing")
            if (running && preview.pending) classes("badge-streak-pending")
            attr("title", streakTitle(level, carried, preview.pending))
        }) {
            if (index == flame) Text("🔥 ") else if (running && preview.pending && index == shown.first()) Text("❄ ")
            Span(attrs = { classes("badge-streak-label") }) { Text(level.label) }
            Text(" ${level.streak}")
        }
    }
}

private fun streakTitle(level: StreakLevel, carriedToday: Boolean, pending: Boolean): String = buildString {
    append(level.label).append(": ")
    append(
        when {
            level.streak == 0 -> "řada neběží"
            pending -> "${czechDays(level.streak)}, čeká na doplacení"
            carriedToday -> "${czechDays(level.streak)} v kuse, dnes splněno"
            else -> "${czechDays(level.streak)} v kuse, dnes ještě nesplněno"
        },
    )
    append(", nejlepší ").append(level.best)
}

@Composable
fun StatCard(label: String, value: String) {
    Div(attrs = { classes("stat-card") }) {
        Div(attrs = { classes("stat-value") }) { Text(value) }
        Div(attrs = { classes("stat-label") }) { Text(label) }
    }
}

/**
 * Badge colour for a tier. Matches on the label because tiers are editable in the admin screen --
 * a renamed or brand-new tier simply falls through to the neutral colour instead of breaking.
 */
/** Streak badge colour, matched to the tier the level belongs to -- see [tierBadgeClass]. */
fun streakBadgeClass(label: String): String = when {
    label.contains("LEMRA", ignoreCase = true) -> "streak-lemra"
    label.contains("SUPERBOREC", ignoreCase = true) -> "streak-superborec"
    label.contains("ARCIBOREC", ignoreCase = true) -> "streak-arciborec"
    label.contains("BOREC", ignoreCase = true) -> "streak-borec"
    else -> "streak-other"
}

fun tierBadgeClass(label: String): String = when {
    label.contains("NEMOC", ignoreCase = true) -> "badge-illness"
    label.contains("LEMRA", ignoreCase = true) -> "badge-lemra"
    label.contains("SUPERBOREC", ignoreCase = true) -> "badge-superborec"
    label.contains("ARCIBOREC", ignoreCase = true) -> "badge-arciborec"
    label.contains("BOREC", ignoreCase = true) -> "badge-borec"
    else -> "badge-other"
}

/**
 * Date picker shared by the daily entry form and the grid: the date itself, the weekday name, and
 * "Dnes"/"Včera" shortcuts -- each hidden when it is already the selected day.
 */
@Composable
fun DateSelector(selectedDate: String, onChangeDate: (String) -> Unit) {
    Div(attrs = { classes("date-row") }) {
        Span { Text("Datum") }
        Input(type = InputType.Date, attrs = {
            value(selectedDate)
            onInput { if (it.value.isNotBlank()) onChangeDate(it.value) }
        })
        Span(attrs = { classes("day-name") }) { Text(dayNameFor(selectedDate)) }
        if (selectedDate != todayIso()) {
            Button(attrs = { onClick { onChangeDate(todayIso()) } }) { Text("Dnes") }
        }
        if (selectedDate != yesterdayIso()) {
            Button(attrs = { onClick { onChangeDate(yesterdayIso()) } }) { Text("Včera") }
        }
    }
}

/** "Uložit" plus the confirmation that follows a successful save of [forDate]. */
@Composable
fun SaveRow(label: String, busy: Boolean, savedDate: String?, forDate: String, onSave: () -> Unit) {
    PrimaryButton(if (busy) "Ukládám…" else label, enabled = !busy, onClick = onSave)
    if (savedDate == forDate) P(attrs = { classes("saved-text") }) { Text("Uloženo ✓") }
}
